package com.sddamico.swigmore.components.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.core.SectionHeader
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.SelectField
import com.sddamico.swigmore.ds.forms.TextField
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.MemberDto
import com.sddamico.swigmore.models.PollTeamDto
import com.sddamico.swigmore.models.SaveMemberRequest
import com.sddamico.swigmore.util.rememberAction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.window

private const val MEMBER_COLUMNS = "minmax(0,1.2fr) minmax(0,1fr) auto"

/** The voter list: who can sign in and vote, and which Fantrax team each owns. */
@Composable
fun MembersPanel(
  members: List<MemberDto>,
  teams: List<PollTeamDto>,
  save: suspend (SaveMemberRequest) -> Unit,
  remove: suspend (MemberDto) -> Unit,
) {
  val request = rememberAction()
  val teamsById = remember(teams) { teams.associateBy { it.id } }
  val sorted = remember(teams) { teams.sortedBy { it.name.lowercase() } }
  // Another owner's team stays pickable (a team can change hands) but says whose it is.
  fun teamOptions(selected: String?) = sorted.map { team ->
    team.id to (team.ownerName?.takeIf { team.id != selected }?.let { "${team.name} · $it" } ?: team.name)
  }
  var email by remember { mutableStateOf("") }
  var name by remember { mutableStateOf("") }
  var teamId by remember { mutableStateOf<String?>(null) }
  // The saved email of the member being edited, and the draft. The form opens in their row; with 30
  // members a form at the foot of the list is off screen.
  var editing by remember { mutableStateOf<String?>(null) }
  var draft by remember { mutableStateOf(MemberDto("", "", null)) }

  SectionHeader("League Members", caption = "${teams.count { it.ownerName != null }} of ${teams.size} teams linked")
  Card(variant = CardVariant.Plain, padding = "0", modifier = Modifier.css("overflow" to "hidden")) {
    Div(
      attrs = Modifier.typeCaps("9px", ".16em")
        .css("display" to "grid", "grid-template-columns" to MEMBER_COLUMNS, "gap" to "12px",
          "padding" to "10px 16px", "background" to SwColor.creamDeep)
        .toAttrs()
    ) {
      Span { Text("Owner") }
      Span { Text("Team") }
      Span(attrs = Modifier.css("text-align" to "right").toAttrs()) { Text("Actions") }
    }
    if (members.isEmpty()) {
      Div(attrs = Modifier.typeBody("14px", 500, "1.4").css("padding" to "14px 16px", "border-top" to "1px solid ${SwColor.ruleOnCream}").toAttrs()) {
        Text("No members yet. Owners can't sign in or vote until they're added here.")
      }
    }
    members.forEach { member ->
      if (editing == member.email) {
        MemberForm(
          title = "Edit member",
          email = draft.email, onEmail = { draft = draft.copy(email = it) },
          name = draft.displayName, onName = { draft = draft.copy(displayName = it) },
          teamId = draft.teamId, onTeam = { draft = draft.copy(teamId = it) },
          teamOptions = teamOptions(draft.teamId),
          error = request.error,
          saveLabel = "Save member",
          canSave = !request.busy && draft.email.isNotBlank() && draft.displayName.isNotBlank() && draft.teamId != null,
          onSave = {
            request.run {
              save(SaveMemberRequest(draft.email.trim(), draft.displayName.trim(), draft.teamId, previousEmail = member.email))
              editing = null
            }
          },
          onCancel = { editing = null; request.error = null },
          busy = request.busy,
        )
        return@forEach
      }
      Div(
        attrs = Modifier.css("display" to "grid", "grid-template-columns" to MEMBER_COLUMNS, "gap" to "12px",
          "align-items" to "center", "padding" to "11px 16px", "border-top" to "1px solid ${SwColor.ruleOnCream}").toAttrs()
      ) {
        Div(attrs = Modifier.css("min-width" to "0").toAttrs()) {
          Div(attrs = Modifier.typeBody("14px", 700, "1.3").toAttrs()) { Text(member.displayName) }
          Div(attrs = Modifier.typeMono("11px").css("opacity" to ".7", "overflow-wrap" to "anywhere", "margin-top" to "2px").toAttrs()) {
            Text(member.email)
          }
        }
        Div(attrs = Modifier.typeCaps("12px", "0").css("min-width" to "0").toAttrs()) {
          Text(member.teamId?.let { teamsById[it]?.name ?: it } ?: "—")
        }
        Div(attrs = Modifier.css("display" to "flex", "gap" to "6px").toAttrs()) {
          Button("Edit", variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm, disabled = request.busy, onClick = {
            editing = member.email; draft = member; request.error = null
          })
          Button("Remove", variant = ButtonVariant.Field, style = ButtonStyle.CapsSm, disabled = request.busy, onClick = {
            if (window.confirm("Remove ${member.displayName}? They won't be able to sign in or vote; past ballots stay.")) request.run {
              remove(member)
            }
          })
        }
      }
    }
    MemberForm(
      title = "Add a member",
      email = email, onEmail = { email = it },
      name = name, onName = { name = it },
      teamId = teamId, onTeam = { teamId = it },
      teamOptions = teamOptions(teamId),
      error = request.error.takeIf { editing == null },
      saveLabel = "Add member",
      canSave = !request.busy && email.isNotBlank() && name.isNotBlank() && teamId != null,
      onSave = { request.run { save(SaveMemberRequest(email.trim(), name.trim(), teamId)); email = ""; name = ""; teamId = null } },
      onCancel = null,
      busy = request.busy,
    )
  }
}

/** The add form, and the edit form that replaces a member's row. */
@Composable
private fun MemberForm(
  title: String,
  email: String,
  onEmail: (String) -> Unit,
  name: String,
  onName: (String) -> Unit,
  teamId: String?,
  onTeam: (String?) -> Unit,
  teamOptions: List<Pair<String, String>>,
  error: String?,
  saveLabel: String,
  canSave: Boolean,
  onSave: () -> Unit,
  onCancel: (() -> Unit)?,
  busy: Boolean,
) {
  Div(attrs = Modifier.css("padding" to "16px", "border-top" to "1px solid ${SwColor.ruleOnCream}", "background" to SwColor.paper).toAttrs()) {
    Div(attrs = Modifier.typeCaps("10px", ".18em").css("margin-bottom" to "10px").toAttrs()) { Text(title) }
    Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(200px,1fr))", "gap" to "8px").toAttrs()) {
      TextField(email, onEmail, placeholder = "owner@example.com", type = "email", label = "Email")
      TextField(name, onName, placeholder = "Name", label = "Name")
      SelectField(teamId, onTeam, teamOptions, placeholder = "Their Fantrax team", label = "Team")
    }
    error?.let { Div(attrs = Modifier.typeMono("11px").css("margin-top" to "10px").toAttrs()) { Text(it) } }
    Div(attrs = Modifier.css("display" to "flex", "gap" to "8px", "margin-top" to "12px", "flex-wrap" to "wrap").toAttrs()) {
      Button(saveLabel, variant = ButtonVariant.Field, style = ButtonStyle.Caps, disabled = !canSave, onClick = onSave)
      if (onCancel != null) Button("Cancel", variant = ButtonVariant.Ghost, style = ButtonStyle.Caps, disabled = busy, onClick = onCancel)
    }
  }
}
